import { API_CONFIG } from '../config/api.config';
import { fetchAPI } from './api';
import { emitUnauthorized } from '../utils/authEvents';

// Supprimer cette ligne avec le hardcodage
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface ApiKey {
  id: number;
  name: string;
  service: string;
  provider: string;
  keyValue: string;
  description?: string;
  isActive: boolean;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApiKeyData {
  name: string;
  service: string;
  provider: string;
  keyValue: string;
  description?: string;
}

export interface UpdateApiKeyData {
  name?: string;
  service?: string;
  provider?: string;
  keyValue?: string;
  description?: string;
  isActive?: boolean;
}

class ApiKeyService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken'); // Utiliser 'accessToken' comme dans api.ts
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  private getAdminHeaders(): Record<string, string> {
    const adminToken = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      ...(adminToken && { 'Authorization': `Bearer ${adminToken}` })
    };
  }

  private getImpersonateUserId(): string | null {
    return localStorage.getItem('adminImpersonateUserId');
  }

  async getApiKeys(): Promise<ApiKey[]> {
    try {
      console.log('🔍 [apiKeyService] Début getApiKeys()');
      const impersonateUserId = this.getImpersonateUserId();
      const url = impersonateUserId
        ? API_CONFIG.getApiUrl(`/admin/users/${impersonateUserId}/api-keys`)
        : API_CONFIG.getApiUrl('/api/apiKeys');
      console.log('🔍 [apiKeyService] URL:', url);
      
      const headers = impersonateUserId ? this.getAdminHeaders() : this.getAuthHeaders();
      console.log('🔍 [apiKeyService] Headers:', headers);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      console.log('🔍 [apiKeyService] Response status:', response.status);
      console.log('🔍 [apiKeyService] Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.status === 401) {
        console.error('❌ [apiKeyService] Session expirée');
        emitUnauthorized({ source: 'apiKeyService', endpoint: url });
        throw new Error('Session expirée');
      }

      if (!response.ok) {
        console.error('❌ [apiKeyService] Response not OK:', response.status);
        const error = await response.json();
        console.error('❌ [apiKeyService] Error details:', error);
        throw new Error(error.error || 'Erreur lors de la récupération des clés API');
      }

      const data = await response.json();
      console.log('✅ [apiKeyService] Data received:', data);
      const keys = Array.isArray(data) ? data : (data?.keys || []);
      console.log('✅ [apiKeyService] Data type:', Array.isArray(keys) ? 'array' : typeof keys);
      console.log('✅ [apiKeyService] Data length:', keys?.length);
      
      return keys;
    } catch (error) {
      console.error('❌ [apiKeyService] Error in getApiKeys:', error);
      throw error;
    }
  }

  async createApiKey(data: CreateApiKeyData): Promise<ApiKey> {
    try {
      const response = await fetch(API_CONFIG.getApiUrl('/api/apiKeys'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (response.status === 401) {
        emitUnauthorized({ source: 'apiKeyService', endpoint: '/api/apiKeys' });
        throw new Error('Session expirée');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la création de la clé API');
      }

      return result;
    } catch (error) {
      console.error('Erreur lors de la création de la clé API:', error);
      throw error;
    }
  }

  async updateApiKey(id: number, data: UpdateApiKeyData): Promise<ApiKey> {
    try {
      const response = await fetch(API_CONFIG.getApiUrl(`/api/apiKeys/${id}`), {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (response.status === 401) {
        emitUnauthorized({ source: 'apiKeyService', endpoint: `/api/apiKeys/${id}` });
        throw new Error('Session expirée');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la mise à jour de la clé API');
      }

      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la clé API:', error);
      throw error;
    }
  }

  async deleteApiKey(id: number): Promise<void> {
    try {
      const response = await fetch(API_CONFIG.getApiUrl(`/api/apiKeys/${id}`), {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        emitUnauthorized({ source: 'apiKeyService', endpoint: `/api/apiKeys/${id}` });
        throw new Error('Session expirée');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de la suppression de la clé API');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la clé API:', error);
      throw error;
    }
  }

  async markAsUsed(id: number): Promise<void> {
    try {
      const response = await fetch(API_CONFIG.getApiUrl(`/api/apiKeys/${id}/use`), {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        emitUnauthorized({ source: 'apiKeyService', endpoint: `/api/apiKeys/${id}/use` });
        throw new Error('Session expirée');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de la mise à jour de l\'utilisation');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'utilisation:', error);
      throw error;
    }
  }

  // Méthode utilitaire pour récupérer une clé spécifique par service
  async getApiKeyByService(service: string): Promise<ApiKey | null> {
    try {
      const keys = await this.getApiKeys();
      return keys.find(key => key.service.toLowerCase() === service.toLowerCase() && key.isActive) || null;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la clé pour ${service}:`, error);
      return null;
    }
  }

  async getApiKeyValueById(apiKeyId: number | string): Promise<string | null> {
    const res = await fetchAPI<{ success?: boolean; data?: { keyValue?: string } }>(`/api/apiKeys/public/${apiKeyId}`);
    return res?.data?.keyValue || null;
  }

  async getGoogleMapsKeyByUserId(userId: number | string): Promise<string | null> {
    const res = await fetchAPI<{ success?: boolean; data?: { keyValue?: string } }>(`/api/apiKeys/public/user/${userId}/maps`);
    return res?.data?.keyValue || null;
  }
}

export const apiKeyService = new ApiKeyService();
export default apiKeyService;
